import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import DocViewer from "@/components/DocViewer";
import ReglamentoVisor, { fechaLarga, fechaHora, imprimirPdf } from "@/components/ReglamentoVisor";
import { BookOpen, Upload, Eye, Users, Send, Trash2, X, FileSpreadsheet, Download } from "lucide-react";

const hoy = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Bogota" });
const input = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-vino-400";
const btnSec = "flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50";
const btnPri = "flex items-center gap-1.5 rounded-lg bg-vino-700 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-vino-800 disabled:opacity-50";
const ESTADOS = { borrador: ["Borrador", "bg-amber-50 text-amber-800"], publicada: ["Vigente", "bg-emerald-50 text-emerald-700"], anterior: ["Versión anterior", "bg-slate-100 text-slate-600"] };

function Modal({ titulo, onClose, children, testid, ancho = "max-w-5xl" }) {
  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-3 sm:p-6" data-testid={testid}>
      <div className={`w-full ${ancho} rounded-2xl bg-white shadow-xl`}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900 md:text-lg">{titulo}</h2>
          <button onClick={onClose} data-testid={`${testid}-cerrar`} aria-label="Cerrar" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4 p-4 sm:p-5">{children}</div>
      </div>
    </div>
  );
}

function CamposVersion({ f, setF, disabled }) {
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <label className="text-xs font-medium text-slate-600">Número de versión<input value={f.version} onChange={set("version")} disabled={disabled} data-testid="rit-campo-version" className={`${input} mt-1`} /></label>
      <label className="text-xs font-medium text-slate-600">Fecha de publicación<input type="date" value={f.fecha_publicacion} onChange={set("fecha_publicacion")} disabled={disabled} data-testid="rit-campo-fecha-publicacion" className={`${input} mt-1`} /></label>
      <label className="text-xs font-medium text-slate-600">Entrada en vigencia<input type="date" value={f.fecha_vigencia} onChange={set("fecha_vigencia")} disabled={disabled} data-testid="rit-campo-fecha-vigencia" className={`${input} mt-1`} /></label>
    </div>
  );
}

function CargarModal({ onClose, onCreada }) {
  const [file, setFile] = useState(null);
  const [f, setF] = useState({ version: "", fecha_publicacion: hoy(), fecha_vigencia: hoy() });
  const [busy, setBusy] = useState(false);
  const enviar = async () => {
    if (!file) return toast.error("Selecciona el archivo del reglamento");
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    setBusy(true);
    try { const { data } = await api.post("/reglamento/versiones", fd); toast.success("Archivo cargado. Revisa la vista previa antes de publicar."); onCreada(data.version); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Modal titulo="Cargar / Actualizar Reglamento" onClose={onClose} testid="rit-cargar-modal" ancho="max-w-2xl">
      <label className="block rounded-xl border-2 border-dashed border-slate-200 p-5 text-center text-sm text-slate-600 hover:border-vino-300">
        <Upload className="mx-auto mb-2 h-6 w-6 text-vino-600" />
        {file ? <span className="font-medium text-slate-800" data-testid="rit-archivo-nombre">{file.name}</span> : "Selecciona un archivo HTML, PDF o Word (.docx)"}
        <input type="file" accept=".html,.htm,.pdf,.docx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="rit-archivo-input" />
      </label>
      <CamposVersion f={f} setF={setF} />
      <p className="text-xs text-slate-500">El archivo queda como borrador. Los empleados lo verán solo cuando confirmes la publicación. Las versiones anteriores, los expedientes y los documentos firmados no se modifican.</p>
      <div className="flex justify-end"><button onClick={enviar} disabled={busy} data-testid="rit-cargar-enviar-btn" className={btnPri}>{busy ? <Spinner className="h-4 w-4" /> : <Eye className="h-4 w-4" />} Cargar y ver vista previa</button></div>
    </Modal>
  );
}

function VersionModal({ v, esAdmin, onClose, onCambio }) {
  const [det, setDet] = useState(null);
  const [f, setF] = useState({ version: v.version, fecha_publicacion: v.fecha_publicacion, fecha_vigencia: v.fecha_vigencia });
  const [busy, setBusy] = useState(false);
  const [verPdf, setVerPdf] = useState(false);
  const borrador = v.estado === "borrador";
  useEffect(() => { api.get(`/reglamento/versiones/${v.id}`).then((r) => setDet(r.data)).catch(() => toast.error("No se pudo cargar la versión")); }, [v.id]);
  const descargar = () => downloadFile(`/reglamento/versiones/${v.id}/pdf`, `reglamento_interno_trabajo_v${v.version}.pdf`).catch(() => toast.error("No se pudo descargar"));
  const publicar = async () => {
    if (!window.confirm(`¿Publicar la versión ${f.version}? Reemplazará la versión vigente en el portal de todos los empleados y se les pedirá confirmar su recepción.`)) return;
    setBusy(true);
    try {
      await api.patch(`/reglamento/versiones/${v.id}`, f);
      await api.post(`/reglamento/versiones/${v.id}/publicar`);
      toast.success(`Versión ${f.version} publicada para todos los empleados`);
      onCambio(); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Modal titulo={borrador && esAdmin ? "Vista previa antes de publicar" : borrador ? `Borrador · versión ${v.version}` : `Versión ${v.version}`} onClose={onClose} testid="rit-version-modal">
      <CamposVersion f={f} setF={setF} disabled={!borrador || !esAdmin} />
      {!det ? <div className="flex h-40 items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div>
        : det.version.formato === "html" ? <ReglamentoVisor html={det.html} membrete={det.membrete} onDescargar={descargar} onImprimir={() => imprimirPdf(`/reglamento/versiones/${v.id}/pdf`).catch(() => toast.error("No se pudo preparar la impresión"))} />
        : <div className="flex gap-2"><button onClick={() => setVerPdf(true)} data-testid="rit-version-ver-pdf" className={btnPri}><Eye className="h-4 w-4" /> Ver PDF</button><button onClick={descargar} className={btnSec}><Download className="h-4 w-4" /> Descargar</button></div>}
      {borrador && esAdmin && (
        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
          <button onClick={publicar} disabled={busy || !det} data-testid="rit-publicar-btn" className={btnPri}>{busy ? <Spinner className="h-4 w-4" /> : <Send className="h-4 w-4" />} Publicar para todos los empleados</button>
        </div>
      )}
      {verPdf && <DocViewer url={`/reglamento/versiones/${v.id}/pdf`} titulo="Reglamento Interno de Trabajo" subtitulo={`Versión ${v.version}`} nombreArchivo={`reglamento_v${v.version}.pdf`} onClose={() => setVerPdf(false)} />}
    </Modal>
  );
}

function ConfirmacionesModal({ v, onClose }) {
  const [d, setD] = useState(null);
  const [filtro, setFiltro] = useState("Todos");
  useEffect(() => { api.get(`/reglamento/versiones/${v.id}/confirmaciones`).then((r) => setD(r.data)).catch(() => toast.error("No se pudieron cargar las confirmaciones")); }, [v.id]);
  const filas = (d?.filas || []).filter((x) => filtro === "Todos" || x.estado === filtro);
  return (
    <Modal titulo={`Confirmaciones · versión ${v.version}`} onClose={onClose} testid="rit-confirmaciones-modal" ancho="max-w-3xl">
      {!d ? <div className="flex h-32 items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div> : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
              {[["Todos", d.filas.length], ["Confirmado", d.confirmados], ["Pendiente", d.pendientes]].map(([k, n]) => (
                <button key={k} onClick={() => setFiltro(k)} data-testid={`rit-filtro-${k.toLowerCase()}`} className={`rounded-md px-3 py-1.5 text-xs font-medium ${filtro === k ? "bg-white text-vino-800 shadow-sm" : "text-slate-600"}`}>{k === "Todos" ? "Todos" : `${k}s`} ({n})</button>
              ))}
            </div>
            <button onClick={() => downloadFile(`/reglamento/versiones/${v.id}/reporte`, `confirmaciones_reglamento_v${v.version}.xlsx`)} data-testid="rit-reporte-excel-btn" className={btnSec}><FileSpreadsheet className="h-4 w-4" /> Descargar Excel</button>
          </div>
          <div className="max-h-[55vh] divide-y divide-slate-100 overflow-y-auto rounded-xl border border-slate-200">
            {filas.length === 0 ? <p className="p-4 text-sm text-slate-400">Sin registros.</p> : filas.map((x) => (
              <div key={x.user_id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm" data-testid={`rit-fila-${x.user_id}`}>
                <div className="min-w-0"><p className="truncate font-medium text-slate-800">{x.nombre || x.email}</p><p className="text-xs text-slate-500">{x.cedula || "Sin identificación"} · {x.email}{x.retirado ? " · Retirado" : ""}</p></div>
                {x.estado === "Confirmado"
                  ? <span className="text-right text-xs text-emerald-700"><span className="font-semibold">Confirmado</span><br />{fechaHora(x.fecha)} · {x.codigo}</span>
                  : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">Pendiente</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}

const plural = (n, s, p) => `${n} ${n === 1 ? s : p}`;

function FilaVersion({ v, total, esAdmin, onVer, onConf, onDescartar }) {
  const [lbl, cls] = ESTADOS[v.estado] || [v.estado, ""];
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`rit-version-${v.id}`}>
      <div className="min-w-0 text-sm">
        <p className="font-semibold text-slate-800">Versión {v.version} <span className={`ml-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{lbl}</span></p>
        <p className="text-xs text-slate-500">Publicación: {fechaLarga(v.fecha_publicacion)} · Vigencia: {fechaLarga(v.fecha_vigencia)} · {v.nombre_archivo}</p>
        {v.estado !== "borrador" && <p className="text-xs text-slate-500" data-testid={`rit-conteo-${v.id}`}>{plural(v.confirmados, "confirmación", "confirmaciones")}{v.estado === "publicada" ? ` de ${total} trabajadores activos` : ""}</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={onVer} data-testid={`rit-ver-${v.id}`} className={btnSec}><Eye className="h-4 w-4" /> {v.estado === "borrador" && esAdmin ? "Revisar y publicar" : "Ver"}</button>
        {v.estado !== "borrador" && <button onClick={onConf} data-testid={`rit-confirmaciones-${v.id}`} className={btnSec}><Users className="h-4 w-4" /> Confirmaciones</button>}
        {v.estado === "borrador" && esAdmin && <button onClick={onDescartar} data-testid={`rit-descartar-${v.id}`} className={btnSec}><Trash2 className="h-4 w-4" /> Descartar</button>}
      </div>
    </div>
  );
}

export default function Reglamento() {
  const { user } = useAuth();
  const esAdmin = user.role === "admin";
  const [data, setData] = useState(null);
  const [cargar, setCargar] = useState(false);
  const [ver, setVer] = useState(null);
  const [conf, setConf] = useState(null);
  const load = () => api.get("/reglamento/versiones").then((r) => setData(r.data)).catch(() => setData({ versiones: [], trabajadores_activos: 0 }));
  useEffect(() => { load(); }, []);
  const descartar = async (v) => {
    if (!window.confirm(`¿Descartar el borrador de la versión ${v.version}? Nunca fue publicado.`)) return;
    try { await api.delete(`/reglamento/versiones/${v.id}`); toast.success("Borrador descartado"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  if (!data) return <div className="flex h-48 items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div>;
  const vigente = data.versiones.find((v) => v.estado === "publicada");
  return (
    <div className="space-y-6" data-testid="admin-reglamento">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Reglamento Interno de Trabajo</h1>
          <p className="mt-1 text-sm text-slate-500">Documento institucional único: lo que publiques aquí aparece en el portal de todos los empleados.</p>
        </div>
        {esAdmin && <button onClick={() => setCargar(true)} data-testid="rit-cargar-btn" className={btnPri}><Upload className="h-4 w-4" /> Cargar / Actualizar Reglamento</button>}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="rit-vigente-card">
        <div className="flex items-center gap-3"><BookOpen className="h-5 w-5 text-vino-700" /><p className="font-semibold text-slate-800">{vigente ? `Versión vigente: ${vigente.version}` : "Aún no hay una versión publicada"}</p></div>
        <p className="mt-1 text-sm text-slate-500">{vigente ? `Vigente desde el ${fechaLarga(vigente.fecha_vigencia)} · ${vigente.confirmados} de ${data.trabajadores_activos} trabajadores activos han confirmado su recepción.` : "Los empleados verán el reglamento cuando un administrador publique una versión."}</p>
      </div>
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-slate-800 md:text-lg">Historial de versiones</h2>
        <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
          {data.versiones.length === 0 ? <p className="p-4 text-sm text-slate-400">Todavía no se ha cargado ningún reglamento.</p>
            : data.versiones.map((v) => <FilaVersion key={v.id} v={v} total={data.trabajadores_activos} esAdmin={esAdmin} onVer={() => setVer(v)} onConf={() => setConf(v)} onDescartar={() => descartar(v)} />)}
        </div>
      </section>
      {cargar && <CargarModal onClose={() => setCargar(false)} onCreada={(v) => { setCargar(false); load(); setVer(v); }} />}
      {ver && <VersionModal v={ver} esAdmin={esAdmin} onClose={() => setVer(null)} onCambio={load} />}
      {conf && <ConfirmacionesModal v={conf} onClose={() => setConf(null)} />}
    </div>
  );
}
